import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { supabase } from '../../supabaseClient';
import LanguageToggle from '../../components/LanguageToggle';
import { useTrialDays } from '../../utils/trialDays';
import VocationWorksLogo from '../../components/VocationWorksLogo';
import { resolveEmployerId } from '../../utils/resolveEmployerId';

const NAVY      = '#0D1B2A';
const AMBER     = '#F5A623';
const WHITE     = '#FFFFFF';
const BG        = '#F5F6F8';
const GREEN     = '#1D9E75';
const FONT_HEAD = "'Bebas Neue', sans-serif";
const FONT_BODY = "'DM Sans', sans-serif";

// Light = the public pricing page (signed out). Dark = a signed-in employer, using the same
// tokens as the employer dashboard (NAVY page, NAVY_MID cards, translucent inputs/toggles).
const LIGHT = {
  page: BG, text: NAVY, muted: 'rgba(13,27,42,0.55)', faint: 'rgba(13,27,42,0.45)', card: WHITE,
  cardBorder: '1px solid #E2E6EB', cardShadow: '0 2px 16px rgba(0,0,0,0.05)', toggleBg: WHITE,
  toggleBorder: '1.5px solid #E2E6EB', toggleOff: 'rgba(13,27,42,0.5)', promoBg: '#FFF7E8',
  buttonBg: NAVY, buttonText: WHITE, error: '#DC2626',
};
const DARK = {
  page: NAVY, text: WHITE, muted: 'rgba(255,255,255,0.6)', faint: 'rgba(255,255,255,0.55)', card: '#132233',
  cardBorder: '1px solid rgba(255,255,255,0.07)', cardShadow: 'none', toggleBg: 'rgba(255,255,255,0.06)',
  toggleBorder: '1.5px solid rgba(255,255,255,0.12)', toggleOff: 'rgba(255,255,255,0.6)', promoBg: 'rgba(245,166,35,0.12)',
  buttonBg: 'rgba(255,255,255,0.1)', buttonText: WHITE, error: '#F87171',
};

const TIERS = [
  { key: 'basic',    monthly: 199, annual: 1990, featureKeys: ['users1', 'jobs3'] },
  { key: 'standard', monthly: 399, annual: 3990, featureKeys: ['users3', 'jobs6', 'transcription'], highlight: true },
  { key: 'plus',     monthly: 599, annual: 5990, featureKeys: ['users5', 'jobs9', 'transcription', 'featured4'] },
];

export default function EmployerPricing() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const trialDays = useTrialDays();
  const [searchParams] = useSearchParams();
  const foundingCode = searchParams.get('founding');
  const [interval, setInterval_] = useState('month');
  const [loadingTier, setLoadingTier] = useState(null);
  const [error, setError] = useState('');
  // Friendly pre-check only, mirroring the messaging-gate pattern -- the real validation
  // happens server-side in create-checkout-session.js, which never trusts this client state
  // for the actual trial length. Only shown/used when lookup confirms the code is still
  // 'pending' (not already redeemed, revoked, or unknown).
  const [foundingPromo, setFoundingPromo] = useState(null);

  // Signed-in employer with an active or trialing subscription: their tier shows "Current plan"
  // (disabled) instead of a purchase button. Status (and price_id) come from
  // employer_subscriptions; the tier comes from employers.plan_tier, which the Stripe webhook
  // derives from that same price_id (the price-to-tier map is server-only, so the browser can't
  // map price_id itself). Anything unreadable just leaves every tier purchasable, as before.
  const [currentTier, setCurrentTier] = useState(null);
  // null = auth not checked yet; true = signed-in employer (dark, dashboard look); false = public.
  const [dark, setDark] = useState(null);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) { if (!cancelled) setDark(false); return; }
      if (!cancelled) setDark(prev => prev ?? 'pending');
      const employerId = await resolveEmployerId(supabase, session.user.id).catch(() => null);
      if (!cancelled) setDark(!!employerId);
      if (!employerId) return;
      const [{ data: subs }, { data: employerRow }] = await Promise.all([
        supabase.from('employer_subscriptions').select('status, price_id').eq('employer_id', employerId),
        supabase.from('employers').select('plan_tier').eq('id', employerId).maybeSingle(),
      ]);
      const live = (subs || []).find(sub => sub.status === 'active' || sub.status === 'trialing');
      const tier = employerRow?.plan_tier;
      if (!cancelled && live && TIERS.some(x => x.key === tier)) setCurrentTier(tier);
    })();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (!foundingCode) return;
    supabase.rpc('lookup_founding_employer_promo', { p_code: foundingCode }).then(({ data }) => {
      const row = Array.isArray(data) ? data[0] : data;
      if (row && row.status === 'pending') setFoundingPromo(row);
    });
  }, [foundingCode]);

  useEffect(() => {
    const link = document.createElement('link');
    link.rel  = 'stylesheet';
    link.href = 'https://fonts.googleapis.com/css2?family=Bebas+Neue&family=DM+Sans:wght@400;500;600;700&display=swap';
    document.head.appendChild(link);
    const style = document.createElement('style');
    style.textContent = `*, *::before, *::after { box-sizing: border-box; } body { background-color: ${dark ? NAVY : BG}; font-family: ${FONT_BODY}; color: ${dark ? WHITE : NAVY}; margin: 0; }`;
    document.head.appendChild(style);
    return () => { document.head.removeChild(link); document.head.removeChild(style); };
  }, [dark]);

  const th = dark === true ? DARK : LIGHT;

  const handleGetStarted = async (tierKey) => {
    setError('');
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) { navigate('/login'); return; }

    setLoadingTier(tierKey);
    try {
      const res = await fetch('/api/create-checkout-session', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${session.access_token}` },
        body: JSON.stringify({
          tier: tierKey, interval, origin: window.location.origin,
          ...(foundingPromo ? { foundingCode } : {}),
        }),
      });
      const data = await res.json();
      if (!res.ok || !data.url) {
        setError(data.error || t('employer.pricing.genericError'));
        setLoadingTier(null);
        return;
      }
      window.location.href = data.url;
    } catch (err) {
      setError(err.message || t('employer.pricing.genericError'));
      setLoadingTier(null);
    }
  };

  // Signed in, still checking whether this is an employer: neutral dark screen, no light flash.
  if (dark === 'pending') {
    return (
      <div style={{ fontFamily: FONT_BODY, backgroundColor: NAVY, minHeight: '100dvh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div role="status" style={{ color: AMBER, fontSize: '16px' }}>{t('common.loading')}</div>
      </div>
    );
  }
  if (dark === null) return <div style={{ minHeight: '100dvh' }} />;

  return (
    <div style={{ fontFamily: FONT_BODY, backgroundColor: th.page, minHeight: '100dvh' }} data-pricing-theme={dark ? 'dark' : 'light'}>
      <nav style={{
        position: 'sticky', top: 0, zIndex: 200, height: '60px', backgroundColor: NAVY,
        display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0 2rem',
      }}>
        <div onClick={() => navigate('/employer/dashboard')} style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer' }}>
          <VocationWorksLogo variant="reversed" size={36} />
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '18px' }}>
          <LanguageToggle dark />
          <span onClick={() => navigate('/employer/dashboard')} style={{ fontSize: '0.875rem', fontWeight: 600, color: 'rgba(255,255,255,0.55)', cursor: 'pointer' }}>
            {t('employer.postJob.backToDashboard')}
          </span>
        </div>
      </nav>

      <div style={{ maxWidth: '1080px', margin: '0 auto', padding: '2.5rem 1.25rem 4rem', textAlign: 'center' }}>
        <h1 style={{ fontFamily: FONT_HEAD, fontSize: '2.8rem', color: th.text, letterSpacing: '0.04em', marginBottom: '0.5rem' }}>
          {t('employer.pricing.heading')}
        </h1>
        <p style={{ fontSize: '0.95rem', color: th.muted, marginBottom: '2rem' }}>
          {t('employer.pricing.subheading', { days: trialDays })}
        </p>

        {foundingPromo && (
          <div style={{
            display: 'inline-flex', alignItems: 'center', gap: '10px', backgroundColor: th.promoBg,
            border: `1.5px solid ${AMBER}`, borderRadius: '10px', padding: '12px 20px', marginBottom: '2rem',
            fontSize: '0.9rem', fontWeight: 600, color: th.text, maxWidth: '90%',
          }}>
            🎉 {t('employer.pricing.foundingPromoBanner', { months: Math.round(foundingPromo.trial_days_granted / 30) })}
          </div>
        )}

        <div style={{ display: 'inline-flex', alignItems: 'center', gap: '10px', backgroundColor: th.toggleBg, border: th.toggleBorder, borderRadius: '999px', padding: '5px', marginBottom: '2.5rem' }}>
          <button
            onClick={() => setInterval_('month')}
            style={{
              padding: '9px 20px', borderRadius: '999px', border: 'none', cursor: 'pointer', fontWeight: 700, fontSize: '0.85rem', fontFamily: FONT_BODY,
              minHeight: dark ? '44px' : undefined,
              backgroundColor: interval === 'month' ? AMBER : 'transparent', color: interval === 'month' ? NAVY : th.toggleOff,
            }}
          >{t('employer.pricing.monthlyToggle')}</button>
          <button
            onClick={() => setInterval_('year')}
            style={{
              padding: '9px 20px', borderRadius: '999px', border: 'none', cursor: 'pointer', fontWeight: 700, fontSize: '0.85rem', fontFamily: FONT_BODY,
              minHeight: dark ? '44px' : undefined,
              backgroundColor: interval === 'year' ? AMBER : 'transparent', color: interval === 'year' ? NAVY : th.toggleOff,
            }}
          >{t('employer.pricing.annualToggle')}</button>
        </div>

        {error && (
          <p style={{ color: th.error, fontSize: '0.9rem', marginBottom: '1.5rem' }}>{error}</p>
        )}

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: '1.25rem', textAlign: 'left' }}>
          {TIERS.map(tier => {
            const price = interval === 'month' ? tier.monthly : tier.annual;
            const isCurrent = currentTier === tier.key;
            return (
              <div
                key={tier.key}
                style={{
                  backgroundColor: th.card, borderRadius: '16px', padding: '28px 24px',
                  border: tier.highlight ? `2px solid ${AMBER}` : th.cardBorder,
                  boxShadow: tier.highlight ? '0 8px 32px rgba(245,166,35,0.18)' : th.cardShadow,
                  position: 'relative',
                }}
              >
                {tier.highlight && (
                  <span style={{ position: 'absolute', top: '-11px', left: '24px', backgroundColor: AMBER, color: NAVY, fontSize: '0.7rem', fontWeight: 700, padding: '3px 10px', borderRadius: '999px' }}>
                    {t('employer.pricing.mostPopular')}
                  </span>
                )}
                <h2 style={{ fontFamily: FONT_HEAD, fontSize: '1.6rem', letterSpacing: '0.03em', color: th.text, marginBottom: '4px' }}>
                  {t(`employer.pricing.tiers.${tier.key}.name`)}
                </h2>
                <div style={{ marginBottom: '4px' }}>
                  <span style={{ fontSize: '2.1rem', fontWeight: 700, color: th.text }}>${price}</span>
                  <span style={{ fontSize: '0.85rem', color: th.faint }}>{interval === 'month' ? t('employer.pricing.perMonth') : t('employer.pricing.perYear')}</span>
                </div>
                {interval === 'year' && (
                  <p style={{ fontSize: '0.78rem', color: GREEN, fontWeight: 600, marginBottom: '14px' }}>{t('employer.pricing.annualSavingsNote')}</p>
                )}
                {interval === 'month' && <div style={{ marginBottom: '14px' }} />}
                <p style={{ fontSize: '0.8rem', color: th.faint, marginBottom: '16px' }}>{t('employer.pricing.trialNote', { days: trialDays })}</p>

                <ul style={{ listStyle: 'none', padding: 0, margin: '0 0 22px', display: 'flex', flexDirection: 'column', gap: '9px' }}>
                  {tier.featureKeys.map(fk => (
                    <li key={fk} style={{ display: 'flex', alignItems: 'flex-start', gap: '8px', fontSize: '0.86rem', color: th.text }}>
                      <span style={{ color: GREEN, fontWeight: 700 }}>✓</span>
                      {t(`employer.pricing.features.${fk}`)}
                    </li>
                  ))}
                </ul>

                {isCurrent ? (
                  <button
                    type="button"
                    disabled
                    data-current-plan
                    style={{
                      width: '100%', minHeight: '44px', padding: '12px', borderRadius: '8px', cursor: 'default',
                      backgroundColor: 'rgba(29,158,117,0.1)', color: GREEN, border: `1.5px solid ${GREEN}`,
                      fontWeight: 700, fontSize: '0.9rem', fontFamily: FONT_BODY,
                    }}
                  >
                    {t('employer.pricing.currentPlan')}
                  </button>
                ) : (
                  <button
                    onClick={() => handleGetStarted(tier.key)}
                    disabled={loadingTier === tier.key}
                    style={{
                      width: '100%', minHeight: '44px', padding: '12px', borderRadius: '8px', border: 'none', cursor: loadingTier === tier.key ? 'default' : 'pointer',
                      backgroundColor: tier.highlight ? AMBER : th.buttonBg, color: tier.highlight ? NAVY : th.buttonText,
                      fontWeight: 700, fontSize: '0.9rem', fontFamily: FONT_BODY, opacity: loadingTier === tier.key ? 0.7 : 1,
                    }}
                  >
                    {loadingTier === tier.key ? t('employer.pricing.redirecting') : t('employer.pricing.getStartedButton')}
                  </button>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
